import * as React from "react";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  CheckCircle2,
  CircleDot,
  Database,
  ExternalLink,
  Github,
  Loader,
  LockKeyhole,
  Save,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import "@xyflow/react/dist/style.css";

import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ProjectContext,
  ProjectContextType,
  ProjectType,
} from "@/context/project";

import { Alert } from "./ui/alert";
import { UserContext } from "@/context/user";
import { DeploymentSettings } from "./deployment-settings";
import { ProjectPaymentPopup } from "./project-payment-popup";
import { RedeploymentSettings } from "./redeployment-settings";
import { UploadedFile } from "./configuration-files";
import { Progress } from "@/components/ui/progress";
import { SshKeyGenerator } from "./ssh-key-generator";
import { ProjectNotFound } from "./project-not-found";
import { appSchema, AppContextType } from "@/context/app";
import { nodes, templates, appConfigs, templateTypeMapping } from "@/config/nodes";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FlowWidget } from "@/components/widgets/FlowWidget";

const edgeSchema = z.object({
  source: z.string(),
  target: z.string(),
  data: z.object({
    deploymentUrl: z.boolean().optional(),
    allowCors: z.boolean().optional(),
  }),
});

const appsSchema = z.array(appSchema);
const edgesSchema = z.array(edgeSchema);

const formSchema = z.object({
  name: z.string().min(3, {
    message: "Project Name must be at least 3 characters.",
  }),
  domainName: z.string().min(3, {
    message: "Domain Name must be at least 3 characters.",
  }),
  apps: appsSchema,
  edges: edgesSchema,
});

type FormValues = z.infer<typeof formSchema>;

const allMilestones = [
  { id: "create", name: "Project Created" },
  { id: "generate", name: "Generate Repositories" },
  { id: "generate-ssh-keys", name: "Generate SSH Keys" },
  { id: "deploy", name: "Deploy & Manage" },
];

const ProjectMilestones = () => {
  const { user } = React.useContext(UserContext);
  const { projectsMap, setProjectsMap, setActiveProject }: ProjectContextType =
    React.useContext<ProjectContextType>(ProjectContext);
  const navigate = useNavigate();
  const params = useParams();
  const projectId = params?.projectId || "";
  const projectFromMap = projectsMap[projectId];

  const [project, setProject] = React.useState(projectFromMap);
  const [, setCreating] = useState<boolean>(false);
  const [completedMilestones, setCompletedMilestones] = useState<string[]>([]);
  const [openMilestoneId, setOpenMilestoneId] = React.useState<string | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generateMessage, setGenerateMessage] = React.useState("");
  const [generationProgress, setGenerationProgress] = React.useState(0);
  const [jobId, setJobId] = React.useState<string | null>(null);
  const [generationFailed, setGenerationFailed] = React.useState(false);
  const [isDeploying, setIsDeploying] = React.useState(false);
  const [deployMessage, setDeployMessage] = React.useState("");
  const [deploymentProgress, setDeploymentProgress] = React.useState(0);
  const [deploymentFailed, setDeploymentFailed] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);
  const [isChecking, setIsChecking] = React.useState(true);
  const [activeViewTab, setActiveViewTab] = React.useState<string>("list");
  const [unsavedChanges, setUnsavedChanges] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [unsavedApps, setUnsavedApps] = React.useState<Record<string, unknown>>({});
  const [unsavedEdges, setUnsavedEdges] = React.useState<Array<{ source: string; target: string; data?: Record<string, unknown> }>>([]);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
  const [pendingTab, setPendingTab] = React.useState<string | null>(null);
  const [pendingGenerate, setPendingGenerate] = React.useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = React.useState(false);

  const formatDeploymentDate = (dateString: string) => {
    const deploymentDate = new Date(dateString);
    const today = new Date();
    const isToday =
      deploymentDate.getDate() === today.getDate() &&
      deploymentDate.getMonth() === today.getMonth() &&
      deploymentDate.getFullYear() === today.getFullYear();

    if (isToday) {
      return deploymentDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }

    const isThisYear = deploymentDate.getFullYear() === today.getFullYear();
    const dateOptions: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    };

    if (!isThisYear) {
      dateOptions.year = "numeric";
    }

    return `${deploymentDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })} on ${deploymentDate.toLocaleDateString("en-US", dateOptions)}`;
  };
  const getProject = async (projectId: string) => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/project/${projectId}`,
        { credentials: "include" },
      );
      if (res.ok) {
        const { data } = await res.json();
        setProject(data);
        setProjectsMap({ ...projectsMap, [projectId]: data });
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      console.error("Failed to fetch project", error);
      setNotFound(true);
    } finally {
      setIsChecking(false);
    }
  };

  React.useEffect(() => {
    if (projectFromMap) {
      setProject(projectFromMap);
      setIsChecking(false);
      setNotFound(false);
    } else {
      getProject(projectId);
    }
  }, [projectFromMap, projectId]);

  const form = useForm<FormValues>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: project?.name || "",
      domainName: project?.domainName || "",
      apps: project?.apps || [],
      edges: project?.edges || [],
    },
  });

  useEffect(() => {
    if (project?.jobs && !jobId) {
      const runningJob = project.jobs.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (job: unknown) => (job as any).status === "running",
      );
      if (runningJob) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setJobId((runningJob as any).id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((runningJob as any).type === "generate") {
          setIsGenerating(true);
          setGenerateMessage(
            "Repository generation in progress. Estimated time: 5-10 minutes.",
          );
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setGenerationProgress((runningJob as any).progress || 0);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (["deploy", "redeploy"].includes((runningJob as any).type)) {
          setIsDeploying(true);
          setDeployMessage(
            "Deployment in progress. Estimated time: 5-10 minutes.",
          );
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setDeploymentProgress((runningJob as any).progress || 0);
        }
      } else {
        const failedGenerateJob = project.jobs.find(
          (job: unknown) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (job as any).type === "generate" && (job as any).status === "failed",
        );
        if (!isGenerating && failedGenerateJob) {
          setGenerationFailed(true);
          setGenerateMessage(
            `Error: ${
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (failedGenerateJob as any).result?.error?.message || "Generation failed."
            }`,
          );
          setGenerationProgress((failedGenerateJob as { progress?: number }).progress || 0);
        }
        const latestDeployment = project.deployments?.[0];
        if (
          !isDeploying &&
          latestDeployment &&
          latestDeployment.status === "failed"
        ) {
          const failedDeployJob = project.jobs.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (job: unknown) => (job as any).id === latestDeployment.jobId,
          );
          if (failedDeployJob) {
            setDeploymentFailed(true);
            setDeployMessage(
              `Error: ${
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (failedDeployJob as any).result?.error?.message || "Deployment failed."
              }`,
            );
          }
        }
      }
    }
  }, [project, jobId, isGenerating, isDeploying]);

  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(
          `${
            import.meta.env.VITE_PUBLIC_API_ENDPOINT
          }/project/jobs/${jobId}/status`,
          { credentials: "include" },
        );

        if (!res.ok) {
          clearInterval(interval);
          let errorMessage = "Failed to get job status.";
          try {
            const errorResult = await res.json();
            errorMessage = errorResult.error?.message || errorMessage;
          } catch (e) {
            console.log("Error parsing error response JSON:", e);
            errorMessage = res.statusText || errorMessage;
            /* ignore */
          }

          if (isGenerating) {
            setGenerateMessage(`Error: ${errorMessage}`);
            setIsGenerating(false);
            setGenerationFailed(true);
          } else if (isDeploying) {
            setDeployMessage(`Error: ${errorMessage}`);
            setIsDeploying(false);
            setDeploymentFailed(true);
          }
          setJobId(null);
          return;
        }

        const result = await res.json();
        if (result.data) {
          const job = result.data;
          if (job.type === "generate") {
            setGenerationProgress(job.progress || 0);
          } else if (["deploy", "redeploy"].includes(job.type)) {
            setDeploymentProgress(job.progress || 0);
          }

          if (job.status === "completed") {
            clearInterval(interval);
            setJobId(null);

            if (job.type === "generate") {
              setGenerateMessage("Repository generation completed!");
              setProject((prevProject: typeof project) => {
                if (!prevProject) return prevProject;
                const updatedProject = {
                  ...prevProject,
                  generated: true,
                  status: "generated",
                  jobs: [job],
                };
                setProjectsMap({ ...projectsMap, [projectId]: updatedProject });
                return updatedProject;
              });
              setCompletedMilestones((prev) => [...prev, "generate"]);
              setIsGenerating(false);
              setGenerationFailed(false);
            } else if (["deploy", "redeploy"].includes(job.type)) {
              setDeployMessage("Deployment completed!");
              setProject((prevProject: ProjectType) => {
                if (!prevProject) return prevProject;
                const newDeployments = prevProject.deployments?.map(
                  (deployment) => {
                    if (deployment.jobId === job.id) {
                      return {
                        ...deployment,
                        status: "success",
                        progress: 100,
                      };
                    }
                    return deployment;
                  },
                );
                const updatedProject = {
                  ...prevProject,
                  status: job.type === "deploy" ? "provisioned" : "redeployed",
                  jobs: prevProject.jobs?.map((j: unknown) =>
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (j as any).id === job.id ? job : j,
                  ) || [job],
                  deployments: newDeployments,
                };
                setProjectsMap({ ...projectsMap, [projectId]: updatedProject });
                return updatedProject;
              });
              if (job.type === "deploy") {
                setCompletedMilestones([...completedMilestones, "deploy"]);
              }
              setIsDeploying(false);
              setDeploymentFailed(false);
            }
          } else if (job.status === "failed") {
            clearInterval(interval);
            setJobId(null);
            const errorMessage =
              job.result?.error?.message || `${job.type} failed.`;

            if (job.type === "generate") {
              setGenerateMessage(`Error: ${errorMessage}`);
              setIsGenerating(false);
              setGenerationFailed(true);
              setProject((prevProject: typeof project) => {
                if (!prevProject) return prevProject;
                const updatedProject = {
                  ...prevProject,
                  jobs: prevProject.jobs?.map((j: unknown) =>
                    (j as { id: string }).id === job.id ? job : j
                  ) || [job],
                };
                setProjectsMap({ ...projectsMap, [projectId]: updatedProject });
                return updatedProject;
              });
            } else if (["deploy", "redeploy"].includes(job.type)) {
              setDeployMessage(`Error: ${errorMessage}`);
              setIsDeploying(false);
              setDeploymentFailed(true);
              setProject((prevProject: ProjectType) => {
                if (!prevProject) return prevProject;
                const newDeployments = prevProject.deployments?.map(
                  (deployment) => {
                    if (deployment.jobId === job.id) {
                      return { ...deployment, status: "failed", progress: 100 };
                    }
                    return deployment;
                  },
                );
                const updatedProject = {
                  ...prevProject,
                  jobs: prevProject.jobs?.map((j: unknown) =>
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (j as any).id === job.id ? job : j,
                  ) || [job],
                  deployments: newDeployments,
                };
                setProjectsMap({ ...projectsMap, [projectId]: updatedProject });
                return updatedProject;
              });
            }
          }
        }
      } catch (error) {
        console.error("Polling error", error);
        clearInterval(interval);
        setJobId(null);
        const errorMessage = "An unknown error occurred during polling.";
        if (isGenerating) {
          setGenerateMessage(`Error: ${errorMessage}`);
          setIsGenerating(false);
          setGenerationFailed(true);
        } else if (isDeploying) {
          setDeployMessage(`Error: ${errorMessage}`);
          setIsDeploying(false);
          setDeploymentFailed(true);
        }
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [projectId, projectsMap, setProjectsMap, completedMilestones]);

  const addProject = async (payload: FormValues) => {
    form.clearErrors("root.serverError");
    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/project`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      },
    );

    if (response.ok) {
      form.clearErrors("root.serverError");
      const data = await response.json();
      setCreating(false);
      if (data?.data?.id) {
        setProjectsMap({
          ...projectsMap,
          [data?.data?.id]: data?.data,
        });
        navigate(`/projects/${data?.data?.id}`);
      }
    } else if (response.status === 400) {
      setCreating(false);
      const errorData = await response.json();
      form.setError("root.serverError", {
        type: "manual",
        message: errorData?.message || "Failed to create project",
      });
    }
  };

  const onSubmit = (values: FormValues) => {
    const payload = {
      ...project,
      ...values,
    };
    addProject(payload);
  };

  const handleFlowWidgetChange = (state: {
    apps: Record<string, unknown>;
    edges: Array<{ source: string; target: string; data?: Record<string, unknown> }>;
  }) => {
    if (!project) return;

    // Build baseline apps record from current project
    const baseAppsRecord: Record<
      string,
      unknown
    > =
      project.apps?.reduce(
        (acc: Record<string, unknown>, app: unknown) => {
           // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const typedApp = app as any;
          if (typedApp.id && typedApp.template) {
            acc[typedApp.id] = {
              id: typedApp.id,
              name: typedApp.name,
              template: typedApp.template,
              config: typedApp.config || {},
            };
          }
          return acc;
        },
        {} as Record<string, unknown>,
      ) || {};

    const normalizeApps = (appsRecord: Record<string, unknown>) =>
      Object.values(appsRecord)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((app: any) => ({
          id: app.id,
          name: app.name,
          template: app.template,
          config: app.config || {},
        }))
        .sort((a, b) => a.id.localeCompare(b.id));

    const normalizeEdges = (
      edgesArr: Array<{ source: string; target: string; data?: Record<string, unknown> }> | unknown[],
    ) =>
      (edgesArr || [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((edge: any) => ({
          source: edge.source,
          target: edge.target,
          data: edge.data || {},
        }))
        .sort((a, b) =>
          a.source === b.source
            ? a.target.localeCompare(b.target)
            : a.source.localeCompare(b.source),
        );

    const baseAppsNormalized = normalizeApps(baseAppsRecord);
    const newAppsNormalized = normalizeApps(state.apps);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseEdgesNormalized = normalizeEdges(project.edges as any[]);
    const newEdgesNormalized = normalizeEdges(state.edges);

    const appsChanged =
      JSON.stringify(baseAppsNormalized) !== JSON.stringify(newAppsNormalized);
    const edgesChanged =
      JSON.stringify(baseEdgesNormalized) !== JSON.stringify(newEdgesNormalized);

    setUnsavedApps(state.apps);
    setUnsavedEdges(state.edges);
    setUnsavedChanges(appsChanged || edgesChanged);
  };

  const handleSaveProject = async (): Promise<boolean> => {
    if (!project || !projectId || !unsavedChanges) return false;

    form.clearErrors("root.serverError");

    // Validation Logic
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const appsToValidate = Object.values(unsavedApps).length > 0 ? unsavedApps : project?.apps?.reduce((acc: any, app: any) => ({...acc, [app.id]: app}), {});
    const edgesToValidate = unsavedEdges.length > 0 ? unsavedEdges : project?.edges || [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const appsArray = Object.values(appsToValidate || {}) as any[];
    const edgesIsConnected = (sourceId: string, targetId: string) => {
       // eslint-disable-next-line @typescript-eslint/no-explicit-any
       return edgesToValidate.some((e: any) => 
         (e.source === sourceId && e.target === targetId) ||
         (e.target === sourceId && e.source === targetId)
       );
    };

    const authServiceApps = appsArray.filter(app => templateTypeMapping[app.template] === "auth-service");
    const databaseApps = appsArray.filter(app => templateTypeMapping[app.template] === "database");
    
    // 1. Auth Service Requirements
    for (const authApp of authServiceApps) {
        if (!authApp.config?.providers || (authApp.config.providers as string[]).length === 0) {
            form.setError("root.serverError", {
                type: "manual",
                message: `The auth-service node "${authApp.name}" requires at least one OAuth provider.`
            });
            return false;
        }

        const isConnectedToDb = databaseApps.some(db => edgesIsConnected(authApp.id, db.id));
        if (!isConnectedToDb && databaseApps.length > 0) {
             form.setError("root.serverError", {
                type: "manual",
                message: `The auth-service node "${authApp.name}" is not connected to a database.`
            });
            return false;
        }
    }

    // 2. Apps with Auth enabled
    const protectedApps = appsArray.filter(app => app.config?.auth && templateTypeMapping[app.template] !== "auth-service" && templateTypeMapping[app.template] !== "database");
    for (const app of protectedApps) {
        const isConnectedToAuth = authServiceApps.some(auth => edgesIsConnected(app.id, auth.id));
        if (!isConnectedToAuth) {
            form.setError("root.serverError", {
                type: "manual",
                message: `The node "${app.name}" has authentication enabled but is not connected to an auth-service.`
            });
            return false;
        }
    }

    // 3. Apps with DB enabled
    const dbEnabledApps = appsArray.filter(app => app.config?.db && templateTypeMapping[app.template] !== "database");
    for (const app of dbEnabledApps) {
        const isConnectedToDb = databaseApps.some(db => edgesIsConnected(app.id, db.id));
        if (!isConnectedToDb) {
            form.setError("root.serverError", {
                type: "manual",
                message: `The node "${app.name}" has DB connection enabled but is not connected to a database.`
            });
            return false;
        }
    }

    setIsSaving(true);
    try {
      // Convert FlowWidget format to API format
      const appsPayload: Record<string, { name: string; template: string; config: Record<string, unknown> }> = {};
      Object.values(unsavedApps).forEach((app) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typedApp = app as any;
        appsPayload[typedApp.id] = {
          name: typedApp.name,
          template: typedApp.template,
          config: typedApp.config || {},
        };
      });

      const idToName = (id: string) => {
        const app = (unsavedApps[id] as AppContextType) || project?.apps?.find((a) => a.id === id);
        return app?.name || id;
      };

      const edgesPayload = (unsavedEdges || []).map((e) => ({
        ...e,
        source: idToName(e.source),
        target: idToName(e.target),
      }));

      const payload = {
        name: project.name,
        domainName: project.domainName,
        apps: appsPayload,
        edges: edgesPayload,
      };

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/project/${projectId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          credentials: "include",
        },
      );

      if (response.ok) {
        form.clearErrors("root.serverError");
        const data = await response.json();
        if (data?.data) {
          setProject(data.data);
          setProjectsMap({
            ...projectsMap,
            [projectId]: data.data,
          });
          setUnsavedChanges(false);
          setUnsavedApps({});
          setUnsavedEdges([]);
          return true;
        }
      } else {
        const errorData = await response.json();
        form.setError("root.serverError", {
          type: "manual",
          message: errorData?.error?.message || "Failed to save project",
        });
      }
    } catch {
      form.setError("root.serverError", {
        type: "manual",
        message: "An error occurred while saving the project",
      });
    } finally {
      setIsSaving(false);
    }
    return false;
  };

  const handleTabChange = (value: string) => {
    if (unsavedChanges) {
      setPendingTab(value);
      setShowUnsavedDialog(true);
    } else {
      setActiveViewTab(value);
    }
  };

  const generateRepositories = async () => {
    setIsGenerating(true);
    setGenerationFailed(false);
    setGenerateMessage("Initiating generation...");
    setGenerationProgress(0);
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_PUBLIC_API_ENDPOINT
        }/project/${projectId}/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Failed to generate.";
        try {
          const errorResult = JSON.parse(errorText);
          errorMessage = errorResult.error?.message || errorMessage;
          errorMessage = errorResult.error?.message || errorMessage;
        } catch {
          errorMessage = errorText.slice(0, 100) || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.data?.jobId) {
        const newJobId = result.data.jobId;
        setJobId(newJobId);
        setGenerateMessage(
          "Repository generation started! Estimated time: 5-10 minutes.",
        );

        // Optimistically update the project in context with the new job
        const newJob = {
          id: newJobId,
          type: "generate",
          status: "running",
          progress: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          project: projectId,
        };

        const updatedProject = {
          ...project,
          jobs: project.jobs ? [...project.jobs, newJob] : [newJob],
        };
        setProject(updatedProject);
        setProjectsMap({ ...projectsMap, [projectId]: updatedProject });
      } else {
        throw new Error(
          result.error?.message || "Failed to start generation job.",
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred.";
      setGenerateMessage(`Error: ${errorMessage}`);
      setIsGenerating(false);
      setGenerationFailed(true);
    }
  };

  const handleSubscribe = async () => {
    setIsGenerating(true);
    setGenerateMessage("Redirecting to payment...");
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/payments/checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            redirectUrl: window.location.href,
            email: (user?.data as { email: string })?.email,
            name:
              (user?.data as { name: string })?.name ||
              (user?.data as { login: string })?.login,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Failed to redirect to payment.";
        try {
          const errorResult = JSON.parse(errorText);
          errorMessage = errorResult.error?.message || errorMessage;
        } catch {
          errorMessage = errorText.slice(0, 100) || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.url && result.checkoutId) {
        sessionStorage.setItem("checkout_id", result.checkoutId);
        window.location.href = result.url;
      } else {
        throw new Error(result.error || "Failed to get checkout URL.");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred.";
      setGenerateMessage(`Error: ${errorMessage}`);
      setIsGenerating(false);
    }
  };

  const executeGenerate = async () => {
    setOpenMilestoneId(null);
    setGenerateMessage("");
    if (user && !user.subscribed) {
      setShowPaymentPopup(true);
    } else {
      await generateRepositories();
    }
  };

  const handleGenerate = async () => {
    if (unsavedChanges) {
      setPendingGenerate(true);
      setShowUnsavedDialog(true);
    } else {
      await executeGenerate();
    }
  };

  const handleSshKeyGenerated = ({
    sshPublicKey,
  }: {
    sshPublicKey: string;
  }) => {
    setProject((prevProject: typeof project) => {
      if (!prevProject) return prevProject;
      const updatedProject = {
        ...prevProject,
        sshKeysGenerated: true,
        sshPublicKey: sshPublicKey,
      };
      setProjectsMap({ ...projectsMap, [projectId]: updatedProject });
      return updatedProject;
    });
    setCompletedMilestones((prev) => [...prev, "generate-ssh-keys"]);
  };

  const handleRedeploy = async (
    type: string,
    options: {
      environmentVars: { key: string; value: string }[];
      files: UploadedFile[];
    },
  ) => {
    setIsDeploying(true);
    setDeploymentFailed(false);
    setDeployMessage(`Initiating ${type}...`);
    setDeploymentProgress(0);
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_PUBLIC_API_ENDPOINT
        }/project/${projectId}/${type}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(options),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Failed to ${type}.`;
        try {
          const errorResult = JSON.parse(errorText);
          errorMessage = errorResult.error?.message || errorMessage;
        } catch (e) {
          console.log("Error parsing error response JSON:", e);
          errorMessage = errorText.slice(0, 100) || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.data?.jobId) {
        const newJobId = result.data.jobId;
        setJobId(newJobId);
        setDeployMessage(`Deployment started! Estimated time: 5-10 minutes.`);

        const newDeployment = {
          id: `temp-${newJobId}`,
          job: "monorepo-redeploy-v2",
          status: "pending",
          createdAt: new Date().toISOString(),
          jobId: newJobId,
          progress: 0,
          data: {},
          project: projectId,
        };

        const newJob = {
          id: newJobId,
          type,
          status: "running",
          progress: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          project: projectId,
        };

        const updatedProject = {
          ...project,
          jobs: project.jobs ? [...project.jobs, newJob] : [newJob],
          deployments: project.deployments
            ? [newDeployment, ...project.deployments]
            : [newDeployment],
        };
        setProject(updatedProject);
        setProjectsMap({ ...projectsMap, [projectId]: updatedProject });
      } else {
        throw new Error(
          result.error?.message || `Failed to start ${type} job.`,
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred.";
      setDeployMessage(`Error: ${errorMessage}`);
      setIsDeploying(false);
      setDeploymentFailed(true);
    }
  };

  const handleProvisionAndDeploy = async (config: Record<string, unknown>) => {
    setIsDeploying(true);
    setDeploymentFailed(false);
    setDeployMessage("Initiating provisioning and deployment...");
    setDeploymentProgress(0);
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_PUBLIC_API_ENDPOINT
        }/project/${projectId}/provision`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(config),
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Failed to provision.`;
        try {
          const errorResult = JSON.parse(errorText);
          errorMessage = errorResult.error?.message || errorMessage;
        } catch {
          errorMessage = errorText.slice(0, 100) || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.data?.jobId) {
        const newJobId = result.data.jobId;
        setJobId(newJobId);
        setDeployMessage(`Provisioning started! Estimated time: 5-10 minutes.`);

        const newDeployment = {
          id: `temp-${newJobId}`,
          job: "monorepo-deploy-v3",
          status: "pending",
          createdAt: new Date().toISOString(),
          jobId: newJobId,
          progress: 0,
          data: {},
          project: projectId,
        };

        const newJob = {
          id: newJobId,
          type: "provision",
          status: "running",
          progress: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          project: projectId,
        };

        const updatedProject = {
          ...project,
          jobs: project.jobs ? [...project.jobs, newJob] : [newJob],
          deployments: project.deployments
            ? [newDeployment, ...project.deployments]
            : [newDeployment],
        };
        setProject(updatedProject);
        setProjectsMap({ ...projectsMap, [projectId]: updatedProject });
      } else {
        throw new Error(
          result.error?.message || `Failed to start provisioning job.`,
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred.";
      setDeployMessage(`Error: ${errorMessage}`);
      setIsDeploying(false);
      setDeploymentFailed(true);
    }
  };

  React.useEffect(() => {
    setProject(projectFromMap);
    if (projectFromMap) {
      const completed = [];
      if (projectFromMap.createdAt) {
        completed.push("create");
      }
      if (
        projectFromMap.generated ||
        [
          "generated",
          "configured",
          "provisioned",
          "deployed",
          "redeployed",
        ].includes(projectFromMap.status)
      ) {
        completed.push("generate");
      }
      if (
        projectFromMap.sshKeysGenerated ||
        ["configured", "provisioned", "deployed", "redeployed"].includes(
          projectFromMap.status,
        )
      ) {
        completed.push("generate-ssh-keys");
      }
      if (
        ["provisioned", "deployed", "redeployed"].includes(
          projectFromMap.status,
        )
      ) {
        completed.push("deploy");
      }
      setCompletedMilestones(completed);
      // Reset unsaved changes when project updates from external source
      setUnsavedChanges(false);
      setUnsavedApps({});
      setUnsavedEdges([]);
    }
  }, [projectFromMap]);

  React.useEffect(() => {
    if (project) {
      setActiveProject(project);
    }
  }, [project, setActiveProject]);

  React.useEffect(() => {
    const verifyAndGenerate = async () => {
      const checkoutId = sessionStorage.getItem("checkout_id");
      if (checkoutId) {
        setIsGenerating(true);
        setGenerateMessage("Verifying payment...");
        try {
          const response = await fetch(
            `${
              import.meta.env.VITE_PUBLIC_API_ENDPOINT
            }/payments/verify?checkout_id=${checkoutId}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
            },
          );

          if (!response.ok) {
            throw new Error("Payment verification failed.");
          }

          const result = await response.json();
          if (result.subscribed) {
            setGenerateMessage("Payment verified. Starting generation...");
            await generateRepositories();
          } else {
            throw new Error("Subscription not active.");
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "An unknown error occurred.";
          setGenerateMessage(`Error: ${errorMessage}`);
        } finally {
          setIsGenerating(false);
          sessionStorage.removeItem("checkout_id");
        }
      }
    };

    verifyAndGenerate();
  }, []);

  const activeMilestoneId =
    allMilestones[completedMilestones.length]?.id ||
    allMilestones[allMilestones.length - 1].id;

  React.useEffect(() => {
    setOpenMilestoneId(activeMilestoneId);
  }, [activeMilestoneId]);

  const visibleMilestones = allMilestones.slice(
    0,
    completedMilestones.length + 1,
  );

  const renderMilestoneContent = (milestoneId: string) => {
    if (!project) return <div>Loading project...</div>;
    switch (milestoneId) {
      case "create":
        return (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Name
                </p>
                <p className="text-base font-semibold">
                  {project?.name || "N/A"}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Status
                </p>
                <p>
                  <Badge
                    variant="outline"
                    className="bg-sidebar-foreground text-sidebar"
                  >
                    {project?.status || "N/A"}
                  </Badge>
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Domain
                </p>
                <p className="text-base">{project?.domainName || "N/A"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Created At
                </p>
                <p className="text-base">
                  {project?.createdAt
                    ? formatDeploymentDate(project.createdAt)
                    : "N/A"}
                </p>
              </div>
              {completedMilestones.includes("generate") && (
                <div className="col-span-1 space-y-1 md:col-span-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    Monorepo
                  </p>
                  <a
                    href={`https://github.com/${user?.data?.login}/${project?.name}-monorepo`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-base text-blue-500 hover:underline"
                  >
                    <Github size={16} />
                    <span>{`${project?.name}-monorepo`}</span>
                  </a>
                </div>
              )}
            </div>

            <div>
              <h4 className="text-md mb-2 font-semibold">
                Node Repositories ({project?.apps?.length || 0})
              </h4>
              <div className="space-y-2">
                {project?.apps?.map((app) => (
                  <div
                    key={app.id}
                    className="flex items-center justify-between rounded-md border p-3"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{app.name}</span>
                      {completedMilestones.includes("generate") && (
                        <a
                          href={`https://github.com/${user?.data?.login}/${app.name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Github size={16} />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {app.config?.auth && (
                        <span className="flex items-center" title="Auth Enabled">
                          <LockKeyhole className="w-4 dark:text-muted" />
                        </span>
                      )}
                      {app.config?.db && (
                        <span className="flex items-center" title="DB Connection Enabled">
                          <Database className="w-4 dark:text-muted" />
                        </span>
                      )}
                      {app.template && templates[app.template as keyof typeof templates]?.icon && (
                        <span className="flex items-center">
                          {templates[app.template as keyof typeof templates]?.icon}
                        </span>
                      )}
                      {app.type === "auth-service" && app.config?.providers && (
                        <>
                          {(app.config.providers as string[]).map((providerValue) => {
                            const provider = appConfigs.providers.options.find(
                              (opt) => opt.value === providerValue
                            );
                            return provider ? (
                              <span
                                key={providerValue}
                                className="flex items-center"
                                title={provider.label}
                              >
                                {provider.icon}
                              </span>
                            ) : null;
                          })}
                        </>
                      )}
                      <Badge
                        variant="secondary"
                        className="inline-flex gap-2 border-2 border-transparent bg-secondary px-2 py-1 text-accent-foreground"
                      >
                        <span className="flex items-center gap-1">
                          {nodes[app.type as keyof typeof nodes]?.icon} {app.type}
                        </span>
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case "generate": {
        // Convert project apps array to FlowWidget format
        const flowWidgetApps =
          unsavedChanges && Object.keys(unsavedApps).length > 0
            ? (unsavedApps as Record<
                string,
                {
                  id: string;
                  name: string;
                  template: string;
                  config: Record<string, unknown>;
                }
              >)
            : project?.apps?.reduce(
                (acc, app) => {
                  if (app.id && app.template) {
                    acc[app.id] = {
                      id: app.id,
                      name: app.name,
                      template: app.template,
                      config: app.config || {},
                    };
                  }
                  return acc;
                },
                {} as Record<
                  string,
                  {
                    id: string;
                    name: string;
                    template: string;
                    config: Record<string, unknown>;
                  }
                >,
              ) || {};

        const flowWidgetEdges =
          (unsavedChanges ? unsavedEdges : project?.edges || []).map((e) => {
            const allApps = [
              ...(project?.apps || []),
              ...Object.values(unsavedApps as Record<string, AppContextType>),
            ];
            const sourceAppByName = allApps.find((a) => a.name === e.source);
            const targetAppByName = allApps.find((a) => a.name === e.target);

            return {
              ...e,
              source: (sourceAppByName as AppContextType)?.id || e.source,
              target: (targetAppByName as AppContextType)?.id || e.target,
            };
          }) || [];
        const isProjectGenerated = !!project?.generated;

        return (
          <div className="space-y-4 pt-2">
            {generationFailed && generateMessage && (
              <Alert variant="destructive" className="mb-4">
                {generateMessage}
              </Alert>
            )}
            {(isGenerating || generationFailed) && generationProgress > 0 && (
              <div className="w-full pt-2">
                <div className="mb-2 flex items-center gap-4">
                  <div className="align-start flex-1">
                    {generateMessage && (
                      <p
                        className={`text-sm ${generationFailed ? "text-red-500" : "text-green-500"}`}
                      >
                        {generateMessage}
                      </p>
                    )}
                  </div>
                </div>
                <Progress value={generationProgress} className="w-full" />
                <p className="text-right text-sm text-muted-foreground">
                  {generationProgress}%
                </p>
              </div>
            )}
            
            {project?.apps && project.apps.length > 0 && (
              <Tabs value={activeViewTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="gap-1">
                  <TabsTrigger value="list" disabled={isSaving}>List</TabsTrigger>
                  <TabsTrigger value="flow" disabled={isSaving}>Architecture</TabsTrigger>
                </TabsList>
                <TabsContent value="list" className="mt-4">
                  <div>
                    <h4 className="text-md mb-2 font-semibold">
                      Application Repositories
                    </h4>
                    <div className="space-y-2">
                      {project.apps.map((app) => (
                        <div
                          key={app.id}
                          className="flex items-center justify-between rounded-md border p-3"
                        >
                          <div className="flex items-center gap-2">
                            {app?.generated ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                            ) : isGenerating ? (
                              <Loader className="h-5 w-5 animate-spin text-muted-foreground" />
                            ) : (
                              <CircleDot
                                strokeWidth={5}
                                className="h-6 w-6 rounded-full bg-white text-amber-600"
                              />
                            )}
                            <span className="font-medium">{app.name}</span>
                            {app?.generated && (
                              <a
                                href={`https://github.com/${user?.data?.login}/${app.name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:underline"
                              >
                                <Github size={16} />
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {app.config?.auth && (
                              <span className="flex items-center" title="Auth Enabled">
                                <LockKeyhole className="w-4 dark:text-muted" />
                              </span>
                            )}
                            {app.config?.db && (
                              <span className="flex items-center" title="DB Connection Enabled">
                                <Database className="w-4 dark:text-muted" />
                              </span>
                            )}
                            {app.template && templates[app.template as keyof typeof templates]?.icon && (
                              <span className="flex items-center">
                                {templates[app.template as keyof typeof templates]?.icon}
                              </span>
                            )}
                            {app.type === "auth-service" && app.config?.providers && (
                              <>
                                {(app.config.providers as string[]).map((providerValue) => {
                                  const provider = appConfigs.providers.options.find(
                                    (opt) => opt.value === providerValue
                                  );
                                  return provider ? (
                                    <span
                                      key={providerValue}
                                      className="flex items-center"
                                      title={provider.label}
                                    >
                                      {provider.icon}
                                    </span>
                                  ) : null;
                                })}
                              </>
                            )}
                            <Badge
                              variant="secondary"
                              className="inline-flex gap-2 border-2 border-transparent bg-secondary px-2 py-1 text-accent-foreground"
                            >
                              <span className="flex items-center gap-1">
                                {nodes[app.type as keyof typeof nodes]?.icon} {app.type}
                              </span>
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="flow" className="mt-4">
                  <div className="relative h-[600px] w-full rounded-md border">
                    <FlowWidget
                      name={project?.name}
                      domainName={project?.domainName}
                      apps={flowWidgetApps}
                      edges={flowWidgetEdges}
                      mode={isProjectGenerated ? "view-only" : "view-edit"}
                      className="h-full"
                      onChange={!isProjectGenerated ? handleFlowWidgetChange : undefined}
                      onSave={!isProjectGenerated ? handleSaveProject : undefined}
                    />
                    {unsavedChanges && !isProjectGenerated && (
                      <div className="absolute top-4 right-4 z-30">
                        <Button
                          onClick={handleSaveProject}
                          disabled={isSaving}
                          className="shadow-lg"
                        >
                          {isSaving ? (
                            <>
                              <Loader className="mr-2 h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="mr-2 h-4 w-4" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}

            {project?.generated && (
              <div className="col-span-1 space-y-1 md:col-span-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Monorepo
                </p>
                <a
                  href={`https://github.com/${user?.data?.login}/${project?.name}-monorepo`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-base text-blue-500 hover:underline"
                >
                  <Github size={16} />
                  <span>{`${project?.name}-monorepo`}</span>
                  <ExternalLink size={16} />
                </a>
              </div>
            )}
          </div>
        );
      }
      case "generate-ssh-keys":
        return (
          <SshKeyGenerator
            projectId={projectId}
            project={project}
            onSshKeyGenerated={handleSshKeyGenerated}
          />
        );
      case "deploy":
        return completedMilestones.includes("deploy") ? (
          <RedeploymentSettings
            projectId={projectId}
            project={project}
            onRedeploy={handleRedeploy}
            isDeploying={isDeploying}
            deployMessage={deployMessage}
            deploymentProgress={deploymentProgress}
            deploymentFailed={deploymentFailed}
          />
        ) : (
          <DeploymentSettings
            project={project}
            onProvisionAndDeploy={handleProvisionAndDeploy}
            isDeploying={isDeploying}
            deployMessage={deployMessage}
            deploymentProgress={deploymentProgress}
            deploymentFailed={deploymentFailed}
          />
        );
      default:
        return null;
    }
  };

  if (isChecking) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return <ProjectNotFound />;
  }

  return (
    <main className="w-full p-6 py-4">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex h-full flex-col"
        >
          <div className="space-y-2">
            {form.formState.errors.root?.serverError && (
              <Alert variant={"destructive"} className="mb-4 block">
                {form.formState.errors.root.serverError.message}
              </Alert>
            )}
            {visibleMilestones
              .slice(0)
              .reverse()
              .map((milestone, index) => {
                const isActive = activeMilestoneId === milestone.id;
                const isCompleted = completedMilestones.includes(milestone.id);
                const isOpen = openMilestoneId === milestone.id;
                return (
                  <div key={milestone.id} className="relative">
                    <Button
                      type="button"
                      onClick={(e) => {
                        if (!isCompleted && !isActive) {
                          e.stopPropagation();
                          e.preventDefault();
                          return;
                        }
                        // Ensure at least one milestone is always open.
                        // If the milestone is already open, do nothing (prevent closing).
                        // Otherwise, open the clicked milestone.
                        if (!isOpen) {
                          setOpenMilestoneId(milestone.id);
                        }
                      }}
                      variant="ghost"
                      className={`relative h-auto w-full justify-start px-3 py-3 text-left text-primary ${
                        (isGenerating && milestone.id === "generate") ||
                        (isDeploying && milestone.id === "deploy")
                          ? "border border-sidebar-border bg-sidebar"
                          : isOpen
                            ? "border border-sidebar-border bg-sidebar"
                            : "border border-sidebar-border"
                      } group flex flex-col items-start transition-all duration-300 hover:bg-sidebar`}
                    >
                      <div className="relative flex h-9 w-full items-center">
                        <span className="relative z-5 mr-3 flex h-6 w-6 items-center justify-center">
                          {isCompleted ? (
                            <CheckCircle2
                              strokeWidth={3}
                              className="h-6 w-6 rounded-full bg-white text-green-600"
                            />
                          ) : (
                            <CircleDot
                              strokeWidth={5}
                              className="h-6 w-6 rounded-full bg-white text-amber-600"
                            />
                          )}
                        </span>
                        <div className="flex-grow">
                          <span className="block text-primary">
                            {milestone.id === "generate" && isCompleted
                              ? "Project Generated"
                              : milestone.name}
                          </span>
                        </div>
                        {milestone.id === "generate" &&
                          !(
                            project?.generated ||
                            [
                              "generated",
                              "configured",
                              "provisioned",
                              "deployed",
                              "redeployed",
                            ].includes(project?.status)
                          ) && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 pl-2"
                            >
                              <Button
                                className={`align-end gap-1 ${
                                  user && !user.subscribed
                                    ? "border border-[#49ffc8] bg-[#49ffc8] text-slate-600 hover:border-slate-900 hover:bg-[#49ffc8] hover:text-slate-950"
                                    : ""
                                }`}
                                onClick={handleGenerate}
                                disabled={isGenerating}
                              >
                                {isGenerating ? (
                                  <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    Generating...
                                  </>
                                ) : generationFailed ?
                                  "Retry Generation"
                                 : user && !user?.subscribed ? "Subscribe to Generate & Deploy" : "Generate Project Repositories"}
                              </Button>
                            </div>
                          )}
                      </div>
                      {index < visibleMilestones.length - 1 && (
                        <div
                          className={`absolute top-10 left-5.75 z-5 h-[calc(100%-12px)] w-0.25 bg-gray-500`}
                        ></div>
                      )}
                      {milestone.id === "generate" && (isGenerating || generationFailed) && (
                        <div className="w-full px-8 pt-2">
                          <div className="mb-2 flex items-center gap-4">
                            <div className="align-start flex-1">
                              {generateMessage && (
                                <p
                                  className={`text-sm ${generationFailed ? "text-red-500" : "text-green-500"}`}
                                >
                                  {generateMessage}
                                </p>
                              )}
                            </div>
                          </div>
                          <Progress
                            value={generationProgress}
                            className="w-full"
                          />
                          <p className="text-right text-sm text-muted-foreground">
                            {generationProgress}%
                          </p>
                        </div>
                      )}

                      <div
                        className={`w-full pl-8 ${isOpen ? "block" : "hidden"}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderMilestoneContent(milestone.id)}
                      </div>
                    </Button>
                  </div>
                );
              })}
          </div>
        </form>
      </Form>
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Do you want to save them before proceeding?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving} onClick={() => setShowUnsavedDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isSaving}
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                setUnsavedChanges(false);
                setUnsavedApps({});
                setUnsavedEdges([]);
                if (pendingTab) {
                  setActiveViewTab(pendingTab);
                  setPendingTab(null);
                }
                if (pendingGenerate) {
                  executeGenerate();
                  setPendingGenerate(false);
                }
                setShowUnsavedDialog(false);
              }}
            >
              Discard
            </AlertDialogAction>
            <AlertDialogAction
              disabled={isSaving}
              onClick={async (e) => {
                e.preventDefault();
                const success = await handleSaveProject();
                if (success) {
                  if (pendingTab) {
                    setActiveViewTab(pendingTab);
                    setPendingTab(null);
                  }
                  if (pendingGenerate) {
                    executeGenerate();
                    setPendingGenerate(false);
                  }
                }
                setShowUnsavedDialog(false);
              }}
            >
              {isSaving ? (
                <>
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ProjectPaymentPopup
        open={showPaymentPopup}
        onOpenChange={setShowPaymentPopup}
        onSubscribe={handleSubscribe}
        isProcessing={isGenerating}
      />
    </main>
  );
};

export default ProjectMilestones;
